import * as React from 'react'
import { Navigate, useLocation } from 'react-router'
import { useOrg } from '../organization/useOrg'
import { useOrgId } from '../useOrgId'
import { isOnboardingPath } from '../utils/onboardingWorkflowUtils'

/**
 * Component that handles automatic redirection from standard org routes
 * to parallel onboard routes when an organization is onboarding
 */
export const OnboardingRedirect = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const org = useOrg()
  const orgId = useOrgId()

  // Don't redirect if we're already on an onboarding path
  if (isOnboardingPath(location.pathname)) {
    return <>{children}</>
  }

  // Don't redirect if org is not loaded yet
  if (org === undefined) {
    return <>{children}</>
  }

  // Don't redirect if org is not found
  if (org === null) {
    return <>{children}</>
  }

  // Check if organization is onboarding (isOnboarded === false)
  const isOnboarding = org.isOnboarded === false

  if (isOnboarding) {
    // Build the parallel onboard route
    const currentPath = location.pathname
    const orgBasePath = `/manage/org/${orgId}`

    // Extract the sub-path after the org base path
    const subPath = currentPath.replace(orgBasePath, '')

    // Build the onboard path
    let onboardPath = `${orgBasePath}/onboard`

    // If there's a sub-path, append it to the onboard path
    if (subPath && subPath !== '/') {
      // Remove leading slash if present
      const cleanSubPath = subPath.startsWith('/') ? subPath.slice(1) : subPath
      onboardPath = `${onboardPath}/${cleanSubPath}`
    }

    // Preserve search params and hash
    const search = location.search
    const hash = location.hash

    return <Navigate to={`${onboardPath}${search}${hash}`} replace />
  }

  // If not onboarding, render children normally
  return <>{children}</>
}

/**
 * Higher-order component that wraps routes with onboarding redirection logic
 */
export const withOnboardingRedirect = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => (
    <OnboardingRedirect>
      <Component {...props} />
    </OnboardingRedirect>
  )
  WrappedComponent.displayName = `withOnboardingRedirect(${Component.displayName || Component.name})`
  return WrappedComponent
}
